import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Container, Paper, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from '../../store/store';
import {
  setNickname as setNickNameStore,
  setEmail as setEmailStore,
  setInvitationCode as setInvitationCodeStore,
  setInviter as setInviterStore,
} from '../../store/setupUserSlice';
import { v2api } from '../../api/axios';
import Log from '../../lib/log';

// ログの初期化
const log = new Log('SetupUser');

type onChangeEvent = React.ChangeEvent<HTMLInputElement>;
const EmailValidPattern =
  '^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$';

interface Props {
  valid: (value: boolean) => void;
}
export default function SetupUser(props: Props) {
  log.putLog('info', 'SetupUser started.');
  const dispatch = useDispatch();
  const nicknameRef = useRef<HTMLInputElement>(null);
  const [nickname, setNickname] = useState(
    useSelector((state) => state.setupUser.nickname),
  );
  const [nicknameError, setNicknameError] = useState(true);
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState(
    useSelector((state) => state.setupUser.email),
  );
  const [emailError, setEmailError] = useState(true);
  const invitationCodeRef = useRef<HTMLInputElement>(null);
  const [invitationCode, setInvitationCode] = useState(
    useSelector((state) => state.setupUser.invitationCode),
  );
  const [invitationCodeError, setInvitationCodeError] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // 入力値の検証
  const formValidation = (): boolean => {
    let valid = true;

    const n = nicknameRef?.current;
    if (n) {
      const ok = n.validity.valid;
      setNicknameError(!ok);
      valid &&= ok;
      dispatch(setNickNameStore(n.value));
    }

    const e = emailRef?.current;
    if (e) {
      const ok = e.validity.valid;
      setEmailError(!ok);
      valid &&= ok;
      dispatch(setEmailStore(e.value));
    }

    const i = invitationCodeRef?.current;
    if (i) {
      if (i.value.length > 0) {
        const ok = !invitationCodeError;
        valid &&= ok;
      } else {
        setInvitationCodeError(false);
        valid &&= true;
      }
    }

    setIsValid(valid);
    return valid;
  };

  useEffect(() => {
    // 初回に一回検証する
    formValidation();
  });

  useEffect(() => {
    props.valid(isValid);
  }, [props, isValid]);

  const handleChangeNickname = (e: onChangeEvent) => {
    setNickname(e.target.value);
    formValidation();
  };

  const handleChangeEmail = (e: onChangeEvent) => {
    setEmail(e.target.value);
    formValidation();
  };

  const handleChangeInvitationCode = (e: onChangeEvent) => {
    const code = e.target.value;
    setInvitationCode(code);
    if (code.length > 0) {
      if (code.length === 6) {
        // 招待コードを検索
        try {
          (async () => {
            const postData = {
              invitationCode: code,
            };
            const res = await v2api.post('/get-inviter', postData);
            if (res.data.statusCode === 200) {
              setInvitationCodeError(false);
              dispatch(setInvitationCodeStore(code));
              const body = JSON.parse(res.data.body);
              dispatch(setInviterStore(body.id));
            } else {
              setInvitationCodeError(true);
            }
          })();
        } catch (err) {
          alert('招待コードの検証に失敗しました');
          setInvitationCodeError(true);
        }
      } else {
        setInvitationCodeError(true);
      }
    } else {
      setInvitationCodeError(false);
      dispatch(setInvitationCodeStore(''));
    }

    formValidation();
  };

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
      }}
    >
      <Typography variant='h6' gutterBottom component='div' color={'gray'}>
        まずはじめに、あなたのニックネーム（日本語可）とメールアドレスを教えて下さい。招待コードがある方は、招待コードも入力してください。
      </Typography>
      <Container component='main' maxWidth='xs'>
        <TextField
          margin='normal'
          fullWidth
          required
          inputRef={nicknameRef}
          value={nickname}
          error={nicknameError}
          onChange={handleChangeNickname}
          inputProps={{ required: true }}
          label='ニックネーム'
        />
        <TextField
          margin='normal'
          fullWidth
          required
          inputRef={emailRef}
          value={email}
          error={emailError}
          onChange={handleChangeEmail}
          inputProps={{ required: true, pattern: EmailValidPattern }}
          helperText={emailError && emailRef?.current?.validationMessage}
          label='メールアドレス'
        />
        <TextField
          margin='normal'
          fullWidth
          inputRef={invitationCodeRef}
          value={invitationCode}
          error={invitationCodeError}
          onChange={handleChangeInvitationCode}
          label='招待コード（オプション）'
        />
      </Container>
    </Paper>
  );
}
